import { useEffect, useRef, useState } from 'react';
import { REPORTE_CATALOG, REPORTE_IDS, sortReportes, type ReporteId } from '@lidotel/shared';

/**
 * Selector de QUÉ BLOQUES lleva el reporte completo — el mismo gesto que el
 * `VistaPicker` del dashboard, sobre el catálogo de `@lidotel/shared`.
 *
 * A diferencia del dashboard, la selección NO vive en la URL: aquí no hay nada
 * que compartir ni que guardar como vista, es lo que se va a descargar ahora.
 */
export function ReportePicker({
  elegidos,
  onChange,
}: {
  elegidos: ReporteId[];
  onChange: (ids: ReporteId[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle(id: ReporteId) {
    const next = elegidos.includes(id) ? elegidos.filter((x) => x !== id) : [...elegidos, id];
    onChange(sortReportes(next));
  }

  return (
    <div className="relative" ref={box}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:border-slate-600 hover:text-slate-100"
      >
        Bloques · {elegidos.length}/{REPORTE_IDS.length} ▾
      </button>

      {open && (
        <div className="absolute left-0 z-20 mt-1 max-h-[70vh] w-96 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 p-2 shadow-xl">
          {REPORTE_CATALOG.map((r) => (
            <label
              key={r.id}
              className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={elegidos.includes(r.id)}
                onChange={() => toggle(r.id)}
              />
              <span className="min-w-0">
                <span className="block">
                  {r.label}
                  {r.periodo === 'rango' && (
                    <span className="ml-1 text-[11px] text-slate-500">(usa el rango)</span>
                  )}
                </span>
                <span className="block text-[11px] leading-snug text-slate-500">{r.detalle}</span>
              </span>
            </label>
          ))}

          <div className="mt-1 flex justify-between border-t border-slate-800 px-2 pt-2 text-xs">
            <button
              onClick={() => onChange([...REPORTE_IDS])}
              className="text-slate-400 hover:text-blue-300"
            >
              Todo
            </button>
            <button onClick={() => onChange([])} className="text-slate-400 hover:text-blue-300">
              Nada
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
